import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { Pause, Play } from "lucide-react";
import { cellColor, covers, fetchGrid, findGrid, sampleGrid, snapBounds, type GridBounds, type GridVariable, type MeteoGrid, type MeteoLayer } from "../lib/meteo-grid";

const CELL = 6;
const GRID_SPEED = 2;   // ore di previsione al secondo
const RADAR_SPEED = 1.6; // fotogrammi radar al secondo

type RadarFrame = { time: number; path: string };
type RadarData = { host: string; frames: RadarFrame[] };

function viewBounds(map: L.Map): GridBounds {
  const b = map.getBounds();
  // Zoom al minimo: la vista esce dal mondo, la griglia no.
  return { west: Math.max(-180, b.getWest()), south: Math.max(-80, b.getSouth()), east: Math.min(180, b.getEast()), north: Math.min(80, b.getNorth()) };
}

function GridCanvas({ variable, posRef, onGrid, onStatus }: { variable: GridVariable; posRef: MutableRefObject<number>; onGrid: (grid: MeteoGrid) => void; onStatus: (status: string) => void }) {
  const map = useMap();
  useEffect(() => {
    const pane = map.getPane("meteoGrid") ?? map.createPane("meteoGrid");
    pane.style.zIndex = "450"; pane.style.pointerEvents = "none";
    const canvas = L.DomUtil.create("canvas", "weather-fx", pane) as HTMLCanvasElement;
    const ctx = canvas.getContext("2d")!;
    const small = document.createElement("canvas"), sctx = small.getContext("2d")!;
    let grid: MeteoGrid | undefined = findGrid(viewBounds(map)), cols = 0, rows = 0, fx = new Float32Array(0), fy = new Float32Array(0), lat = new Float32Array(0), lon = new Float32Array(0);
    let dpr = 1, dirty = true, frame = 0, lastPos = -1, loadTimer = 0, controller: AbortController | undefined, retry = 0, disposed = false;

    const layout = () => {
      const size = map.getSize();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = size.x * dpr; canvas.height = size.y * dpr;
      canvas.style.width = `${size.x}px`; canvas.style.height = `${size.y}px`;
      L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
      cols = Math.ceil(size.x / CELL); rows = Math.ceil(size.y / CELL);
      small.width = cols; small.height = rows;
      fx = new Float32Array(cols * rows); fy = new Float32Array(cols * rows); lat = new Float32Array(cols * rows); lon = new Float32Array(cols * rows);
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        const point = map.containerPointToLatLng([(c + .5) * CELL, (r + .5) * CELL]), i = r * cols + c;
        const lng = ((point.lng + 540) % 360) - 180;
        lat[i] = point.lat; lon[i] = lng;
        if (grid) { fx[i] = (lng - grid.west) / (grid.east - grid.west) * (grid.cols - 1); fy[i] = (point.lat - grid.south) / (grid.north - grid.south) * (grid.rows - 1); }
      }
      dirty = true;
    };

    const load = () => {
      const view = viewBounds(map);
      if (grid && covers(grid, view)) return;
      const known = findGrid(view);
      if (known) { grid = known; onGrid(known); layout(); return; }
      controller?.abort();
      controller = new AbortController();
      onStatus("Carico i dati meteo…");
      fetchGrid(snapBounds(view), controller.signal).then(result => {
        if (disposed) return;
        grid = result; retry = 0; onStatus(""); onGrid(result); layout();
      }).catch(cause => {
        if (disposed || (cause instanceof DOMException && cause.name === "AbortError")) return;
        onStatus(cause instanceof Error ? cause.message : "Dati meteo non disponibili");
        if (retry >= 4) return;
        retry++;
        window.clearTimeout(loadTimer); loadTimer = window.setTimeout(load, 4000 * retry);
      });
    };

    const draw = () => {
      frame = requestAnimationFrame(draw);
      if (!grid) return;
      const pos = posRef.current;
      if (!dirty && pos === lastPos) return;
      dirty = false; lastPos = pos;
      const image = sctx.createImageData(cols, rows);
      for (let i = 0; i < cols * rows; i++) cellColor(variable, sampleGrid(grid, variable, fx[i]!, fy[i]!, pos), lat[i]!, lon[i]!, image.data, i * 4);
      sctx.putImageData(image, 0, 0);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
      ctx.drawImage(small, 0, 0, cols * CELL * dpr, rows * CELL * dpr);
    };

    const moved = () => { L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0])); layout(); window.clearTimeout(loadTimer); loadTimer = window.setTimeout(load, 350); };
    if (grid) onGrid(grid);
    layout(); load();
    map.on("move zoom viewreset resize", moved);
    frame = requestAnimationFrame(draw);
    return () => {
      disposed = true; controller?.abort(); window.clearTimeout(loadTimer); cancelAnimationFrame(frame);
      map.off("move zoom viewreset resize", moved);
      canvas.remove();
    };
  }, [map, variable, posRef, onGrid, onStatus]);
  return null;
}

// Pochi livelli alla volta: RainViewer limita le richieste, quindi si carica il fotogramma corrente e il successivo.
// Dopo uno spostamento o uno zoom si scartano gli altri, che si ricaricano man mano che l'animazione prosegue.
function RadarLayer({ data, index }: { data: RadarData; index: number }) {
  const map = useMap();
  const state = useRef<{ layers: Map<number, { layer: L.TileLayer; ready: boolean }>; shown: number }>({ layers: new Map(), shown: -1 });
  const ensure = (i: number) => {
    const known = state.current.layers.get(i);
    if (known) return known;
    const frame = data.frames[i];
    if (!frame) return undefined;
    const layer = L.tileLayer(`${data.host}${frame.path}/256/{z}/{x}/{y}/2/1_1.png`, { pane: "meteoRadar", opacity: 0, maxNativeZoom: 7, maxZoom: 15, tileSize: 256, keepBuffer: 0, updateWhenZooming: false, crossOrigin: "anonymous" });
    const entry = { layer, ready: false };
    let retried = false;
    layer.on("load", () => { entry.ready = true; show(); });
    // Se RainViewer scarta qualche immagine si riprova una volta dopo qualche secondo.
    layer.on("tileerror", () => { if (retried) return; retried = true; window.setTimeout(() => { if (state.current.layers.get(i) === entry) layer.redraw(); }, 3000); });
    layer.addTo(map);
    state.current.layers.set(i, entry);
    return entry;
  };
  const target = useRef(index);
  target.current = index;
  const show = () => {
    const { layers } = state.current;
    const wanted = layers.get(target.current);
    if (wanted?.ready) state.current.shown = target.current;
    layers.forEach((entry, i) => entry.layer.setOpacity(i === state.current.shown ? .95 : 0));
  };
  useEffect(() => {
    const pane = map.getPane("meteoRadar") ?? map.createPane("meteoRadar");
    pane.style.zIndex = "450"; pane.style.pointerEvents = "none";
    state.current = { layers: new Map(), shown: -1 };
    return () => { state.current.layers.forEach(entry => entry.layer.remove()); state.current.layers.clear(); };
  }, [map, data]);
  useEffect(() => {
    ensure(index); ensure((index + 1) % data.frames.length); show();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, data]);
  useEffect(() => {
    let timer = 0;
    const purge = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        state.current.layers.forEach((entry, i) => { if (i !== state.current.shown && i !== target.current) { entry.layer.remove(); state.current.layers.delete(i); } });
        ensure(target.current); ensure((target.current + 1) % data.frames.length);
      }, 250);
    };
    map.on("zoomend moveend", purge);
    return () => { window.clearTimeout(timer); map.off("zoomend moveend", purge); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, data]);
  return null;
}

const formatTime = (time: number) => new Date(time).toLocaleString("it-IT", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const CHIPS: Array<[MeteoLayer, string]> = [["radar", "Radar"], ["rain", "Piovosità"], ["clouds", "Nuvole"], ["temperature", "Temperature"]];

export function MeteoLive({ layer, onLayer }: { layer: MeteoLayer; onLayer: (layer: MeteoLayer) => void }) {
  const posRef = useRef(0);
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [grid, setGrid] = useState<MeteoGrid>();
  const [radar, setRadar] = useState<RadarData>();
  const [status, setStatus] = useState("");
  const isRadar = layer === "radar";
  const length = isRadar ? radar?.frames.length ?? 0 : grid?.times.length ?? 0;

  useEffect(() => {
    let timer = 0, cancelled = false;
    const load = () => fetch("https://api.rainviewer.com/public/weather-maps.json").then(response => response.json()).then((json: { host: string; radar?: { past?: RadarFrame[]; nowcast?: RadarFrame[] } }) => {
      if (cancelled) return;
      const frames = [...(json.radar?.past ?? []), ...(json.radar?.nowcast ?? [])];
      setRadar(current => current && current.frames.at(-1)?.path === frames.at(-1)?.path ? current : { host: json.host, frames });
    }).catch(() => { if (!cancelled) setStatus(current => current || "Radar non raggiungibile"); }).finally(() => { if (!cancelled) timer = window.setTimeout(load, 5 * 60_000); });
    void load();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, []);

  // Cambio livello: la linea del tempo riparte dal momento più utile (adesso per le previsioni, l'inizio per il radar).
  useEffect(() => {
    posRef.current = isRadar ? 0 : grid?.nowIndex ?? 0; setPos(posRef.current); setStatus("");
  }, [layer, isRadar, grid?.nowIndex]);

  useEffect(() => {
    // L'animazione è il contenuto della sezione: sul telefono il «riduci animazioni» di sistema non deve fermarla.
    if (!playing || length < 2) return;
    let frame = 0, last = performance.now(), shown = -1;
    const speed = isRadar ? RADAR_SPEED : GRID_SPEED;
    const tick = (now: number) => {
      const dt = Math.min(.1, (now - last) / 1000); last = now;
      let next = posRef.current + dt * speed;
      if (next > length - 1) next = 0;
      posRef.current = next;
      const bucket = Math.floor(next * 6);
      if (bucket !== shown) { shown = bucket; setPos(next); }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, length, isRadar]);

  const seek = (value: number) => { posRef.current = value; setPos(value); };
  const variable = layer === "radar" ? undefined : layer;
  const frameIndex = Math.min(Math.max(0, Math.round(pos)), Math.max(0, length - 1));
  const time = isRadar ? radar?.frames[frameIndex]?.time ? radar.frames[frameIndex]!.time * 1000 : undefined : grid ? grid.times[Math.floor(Math.min(pos, grid.times.length - 1))] : undefined;
  const tag = isRadar ? "Radar reale" : grid && Math.abs(pos - grid.nowIndex) < .5 ? "Adesso" : grid && pos < grid.nowIndex ? "Passato" : "Previsione";

  return (
    <div className="map-frame meteo-live">
      <MapContainer center={[42.6, 12.5]} zoom={6} minZoom={2} maxZoom={10} zoomControl={false} className="map-canvas">
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {variable && <GridCanvas variable={variable} posRef={posRef} onGrid={setGrid} onStatus={setStatus} />}
        {isRadar && radar && <RadarLayer data={radar} index={frameIndex} />}
      </MapContainer>
      <div className="meteo-chips" role="radiogroup" aria-label="Livello meteo">{CHIPS.map(([id, label]) => <button key={id} role="radio" aria-checked={layer === id} className={layer === id ? "is-active" : ""} onClick={() => onLayer(id)}>{label}</button>)}</div>
      {status && <div className="viewport-loading"><span className="spinner" /> {status}</div>}
      <div className="meteo-timeline">
        <button className="icon-button" onClick={() => setPlaying(value => !value)} aria-label={playing ? "Pausa" : "Riproduci"}>{playing ? <Pause /> : <Play />}</button>
        <div className="meteo-timeline-body">
          <div className="meteo-timeline-head"><strong>{time ? formatTime(time) : "In attesa dei dati…"}</strong><em className={`meteo-tag ${tag === "Adesso" ? "is-now" : ""}`}>{tag}</em></div>
          <input type="range" min={0} max={Math.max(1, length - 1)} step={isRadar ? 1 : .05} value={isRadar ? frameIndex : pos} disabled={length < 2} onChange={event => { setPlaying(false); seek(Number(event.target.value)); }} aria-label="Momento della mappa" />
        </div>
      </div>
    </div>
  );
}
